import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import ClimateCharts from '../components/ClimateCharts';
import DeviceSettingsPage from './DeviceSettingsPage';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';

export default function DevicePage() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [device, setDevice] = useState(null);

  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const isRtl = dir === 'rtl';

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!token) return;
    authFetch(`/api/devices/${id}`, {}, { token, onUnauthorized: handleUnauthorized })
      .then(async (response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((payload) => {
        if (payload) setDevice(payload);
      })
      .catch((error) => console.error('Failed to load device', error));
  }, [handleUnauthorized, id, token]);

  const activeTab = searchParams.get('tab') === 'settings' ? 'settings' : 'overview';
  const switchTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'settings') next.set('tab', 'settings');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="container-fluid px-3 px-lg-4 mt-3" dir={dir}>
      <div className={`mb-3 ${isRtl ? 'text-end' : ''}`}>
        <LanguageSelector />
      </div>
      <div className={`mb-3 ${isRtl ? 'text-end' : ''}`}>
        <Link to="/devices" className="btn btn-link px-0">
          {isRtl ? `${t('backToControllers')} →` : `← ${t('backToControllers')}`}
        </Link>
      </div>
      <div
        className={
          activeTab === 'settings' ? 'card border-0 bg-transparent shadow-none' : 'card shadow-sm'
        }
      >
        <div className="card-header bg-transparent border-0 pb-0">
          <ul className={`nav nav-tabs card-header-tabs ${isRtl ? 'justify-content-end' : ''}`}>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => switchTab('overview')}
              >
                {t('deviceTabs.overview')}
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => switchTab('settings')}
              >
                {t('deviceTabs.settings')}
              </button>
            </li>
          </ul>
        </div>
        <div className={activeTab === 'settings' ? 'card-body px-0 pt-0' : 'card-body'}>
          {activeTab === 'settings' ? (
            <DeviceSettingsPage embedded deviceId={id} />
          ) : (
            <ClimateCharts
              deviceId={id}
              device={device}
              farmPlan={device?.farmPlan}
              settingsHref={`/device/${id}?tab=settings`}
              editHref={`/device/${id}?tab=settings`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
